import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * The corner components of a card overlay — Media, Elements, Settings,
 * Layout — are one family: a round trigger in each corner, blue when open,
 * and ONLY ONE OPEN AT A TIME. This context is the "one at a time" rule.
 *
 * A card wraps its overlay in <CornerControlsProvider>; each component asks
 * for its own slot with useCornerControl('media'). Without a provider (the
 * original card has only Media) the hook falls back to local state, so the
 * component works alone.
 */

export type CornerControlId = 'media' | 'elements' | 'settings' | 'layout';

interface CornerControlsState {
  openId: CornerControlId | null;
  setOpenId: (id: CornerControlId | null) => void;
}

const CornerControlsContext = createContext<CornerControlsState | null>(null);

interface CornerControlsProviderProps {
  children: React.ReactNode;
  /** Controlled mode: the card owns the open corner (so its own inline
   *  controls, e.g. the settings puck, obey the rule too). */
  openId?: CornerControlId | null;
  onChange?: (id: CornerControlId | null) => void;
}

export const CornerControlsProvider: React.FC<CornerControlsProviderProps> = ({ children, openId, onChange }) => {
  const [localId, setLocalId] = useState<CornerControlId | null>(null);
  const controlled = openId !== undefined;
  const current = controlled ? openId ?? null : localId;
  const setOpenId = useCallback((id: CornerControlId | null) => {
    if (onChange) onChange(id);
    if (!controlled) setLocalId(id);
  }, [controlled, onChange]);
  const value = useMemo(() => ({ openId: current, setOpenId }), [current, setOpenId]);
  return <CornerControlsContext.Provider value={value}>{children}</CornerControlsContext.Provider>;
};

export interface CornerControl {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export const useCornerControl = (id: CornerControlId): CornerControl => {
  const ctx = useContext(CornerControlsContext);
  const [localOpen, setLocalOpen] = useState(false);
  const isOpen = ctx ? ctx.openId === id : localOpen;
  const open = useCallback(() => (ctx ? ctx.setOpenId(id) : setLocalOpen(true)), [ctx, id]);
  const close = useCallback(() => {
    if (ctx) {
      if (ctx.openId === id) ctx.setOpenId(null);
    } else {
      setLocalOpen(false);
    }
  }, [ctx, id]);
  const toggle = useCallback(() => (isOpen ? close() : open()), [isOpen, open, close]);
  return { isOpen, open, close, toggle };
};
